import React, { useMemo } from "react";
import { t } from "@bookingcom/lingojs-core";
import { useI18n } from "@bookingcom/lingojs-react";
import { Text, SheetContainer, Icon } from "@bookingcom/bui-react";
import { useSelector } from "react-redux";
import { getSearchCriteria } from "../../../../store/searchCriteria/selectors.js";
import { QuestionMarkCircleIcon } from "@bookingcom/bui-assets-react/streamline";
import useFormatPrice from "../../../../hooks/useFormatPrice.js";
import useUserAgent from "../../../../hooks/useUserAgent.js";
import usePriceBreakDownForPreOptedAncillaries from "../../../../hooks/usePriceBreakDownForPreOptedAncillaries.js";
import useGlobalContext from "../../../../hooks/useGlobalContext.js";
import getLowestAdultTravellerPrice from "../../../../utils/lowest-adult-traveller-price.js";
import FlightAvailableSeats from "./FlightAvailableSeats.js";
import useEventTracking from "../../../../hooks/useEventTracking.js";
import useRouteName from "../../../../hooks/useRouteName.js";
import styles from "./FlightCardPrice.module.css";
import { getCfarTicketTotalPriceFromFlight, getFlexibleTicketTotalPriceFromFlight } from "../../../../ancillaries/utils/flexibility.js";
import { getExtOriginMarketingTrackingVariable } from "../../../../utils/marketing-url-params.js";
import { localizationUtils } from "@flights/web-api-utils-universal";
import { UnifiedPriceBreakdownFlightCardPrice, useUnifiedPriceBreakdownFlightCardPriceController } from "../../UnifiedPriceBreakdown/index.js";
import { splitBoundsTrackingStorage } from "../../../../utils/experiments/funnel/flights_split_bounds_us.js";
import { UnifiedPriceTag } from "../../UnifiedPriceTag/index.js";
const resetBodyOverflow = () => {
    document.body.style.overflow = "auto";
};
const FlightCardPrice = ({ flight, flightIndex, breakdownOpen, setBreakdownOpen, alignment = "end", totalPriceLabel, showPricePerPerson = false, showTotalPriceIncludingAncillaries = false, showAvailableSeats = false, priceFrom = false, isRenderedInModal = false, isFlexTicket, isCfarTicket, isSmallerTextVariantForMainPrice = false, popoverCloseAriaLabel, totalPriceTagName = "div", boundsVariant, isRounded = false, onPriceBreakdownOpen, variant }) => {
    const { priceBreakdown, travellerPrices, perTravellerPriceDifferences } = flight;
    const i18n = useI18n();
    const { isMobile } = useUserAgent();
    const searchCriteria = useSelector(getSearchCriteria);
    const { adults, children } = searchCriteria;
    const { requestId } = useGlobalContext();
    const { formatPrice } = useFormatPrice();
    const routeName = useRouteName();
    const trackV2 = useEventTracking("flight_details", requestId);
    const isOfMetaOrigin = () => getExtOriginMarketingTrackingVariable();
    const isMeta = isOfMetaOrigin();
    const { getAncillaries, getTotalPrice, getPreOptedAncillaries } = usePriceBreakDownForPreOptedAncillaries(isMeta ? flight : undefined);
    const ancillariesForPriceBreakDown = showTotalPriceIncludingAncillaries ? getPreOptedAncillaries() : getAncillaries();
    const totalPrice = showTotalPriceIncludingAncillaries
        ? getTotalPrice(priceBreakdown, ancillariesForPriceBreakDown)
        : priceBreakdown;
    const adultTravellerPrice = getLowestAdultTravellerPrice(travellerPrices);
    const adultTravellerPriceDifference = perTravellerPriceDifferences?.find(({ travellerType }) => travellerType === "ADULT");
    const { attachedAncillaries } = useUnifiedPriceBreakdownFlightCardPriceController({ flight });
    const shouldShowBreakdown = boundsVariant !== "outbound";
    const roundPrice = isRounded || (!isOfMetaOrigin() && isMobile);
    const isStrikedThroughPricesEnabled = flight.priceBreakdown.showPriceStrikethrough;
    const formattedTotalPrice = useMemo(() => {
        if (isFlexTicket) {
            const flexibleFullPrice = getFlexibleTicketTotalPriceFromFlight(flight);
            return formatPrice(roundPrice && flexibleFullPrice.totalRounded ? flexibleFullPrice.totalRounded : flexibleFullPrice.total, {
                decimalPlaces: roundPrice ? 0 : undefined
            });
        }
        if (isCfarTicket) {
            const cfarFullPrice = getCfarTicketTotalPriceFromFlight(flight);
            if (cfarFullPrice) {
                return formatPrice(roundPrice && cfarFullPrice.totalRounded ? cfarFullPrice.totalRounded : cfarFullPrice.total, {
                    decimalPlaces: roundPrice ? 0 : undefined
                });
            }
        }
        return formatPrice(roundPrice && totalPrice.totalRounded ? totalPrice.totalRounded : totalPrice.total, {
            decimalPlaces: roundPrice ? 0 : undefined
        });
    }, [isFlexTicket, isCfarTicket, formatPrice, flight, roundPrice, totalPrice.totalRounded, totalPrice.total]);
    const formattedPricePerTravellerString = useMemo(() => {
        if (boundsVariant === "inbound") {
            if (!adultTravellerPriceDifference) {
                throw new Error("Split Bounds: Adult perTravellerPriceDifferences was not found");
            }
            const price = roundPrice && adultTravellerPriceDifference.totalRounded
                ? adultTravellerPriceDifference.totalRounded
                : adultTravellerPriceDifference.total;
            if (price.units < 0) {
                splitBoundsTrackingStorage.addValue(flight.token, "price");
            }
            const formatted = formatPrice(price, { decimalPlaces: roundPrice ? 0 : undefined }).replace("-", "");
            return price.units < 0
                ? i18n.trans(t("flights_sr_split_bounds_us_incremental_minus", { variables: { price: formatted } }))
                : i18n.trans(t("flights_sr_split_bounds_us_incremental_plus", { variables: { price: formatted } }));
        }
        if (!adultTravellerPrice)
            return "";
        const price = roundPrice && adultTravellerPrice.travellerPriceBreakdown.totalRounded
            ? adultTravellerPrice.travellerPriceBreakdown.totalRounded
            : adultTravellerPrice.travellerPriceBreakdown.total;
        return formatPrice(price, { decimalPlaces: roundPrice ? 0 : undefined });
    }, [adultTravellerPrice, adultTravellerPriceDifference, boundsVariant, flight.token, formatPrice, i18n, roundPrice]);
    const priceExplanation = useMemo(() => {
        if (localizationUtils.isHungaryDepartingRyanAirFlight(flight.segments)) {
            return i18n.trans(t("flights_ryanair_hungary_sr_price_single"));
        }
        else {
            return totalPriceLabel || i18n.trans(t("flights_fd_price_multi"));
        }
    }, [flight.segments, i18n, totalPriceLabel]);
    const strikedThroughPrice = useMemo(() => isStrikedThroughPricesEnabled && roundPrice && priceBreakdown.totalWithoutDiscountRounded
        ? formatPrice(priceBreakdown.totalWithoutDiscountRounded, { decimalPlaces: 0 })
        : isStrikedThroughPricesEnabled && priceBreakdown.totalWithoutDiscount
            ? formatPrice(priceBreakdown.totalWithoutDiscount)
            : null, [
        formatPrice,
        isStrikedThroughPricesEnabled,
        roundPrice,
        priceBreakdown.totalWithoutDiscount,
        priceBreakdown.totalWithoutDiscountRounded
    ]);
    const priceAriaLabel = useMemo(() => {
        if (showPricePerPerson && !!adultTravellerPrice) {
            const priceLabel = priceFrom
                ? i18n.trans(t("flights_sr_rt_from_price", {
                    variables: {
                        price: formattedPricePerTravellerString
                    }
                }))
                : formattedPricePerTravellerString;
            if (localizationUtils.isHungaryDepartingRyanAirFlight(flight.segments)) {
                return `${priceLabel} ${i18n.trans(t("flights_ryanair_hungary_sr_price_single"))}`;
            }
            if (isStrikedThroughPricesEnabled && strikedThroughPrice) {
                return i18n.trans(t("a11y_flights_deal_price_new", { variables: { new_price: formattedPricePerTravellerString } }));
            }
            return priceLabel;
        }
        const priceLabel = priceFrom
            ? i18n.trans(t("flights_sr_rt_from_price", {
                variables: { price: formattedTotalPrice }
            }))
            : formattedTotalPrice;
        const travellersCount = adults + children.length;
        if (localizationUtils.isHungaryDepartingRyanAirFlight(flight.segments)) {
            return `${priceLabel} ${t("flights_ryanair_hungary_bp_price_per", {
                variables: { total_price: priceLabel, num_travellers: travellersCount }
            })}`;
        }
        if (isStrikedThroughPricesEnabled && strikedThroughPrice) {
            return [
                i18n.trans(t("a11y_flights_deal_price_old_solo", {
                    variables: {
                        old_price: strikedThroughPrice
                    }
                })),
                i18n.trans(t("a11y_flights_deal_price_new", { variables: { new_price: formattedTotalPrice } }))
            ].join(", ");
        }
        return priceLabel;
    }, [
        adultTravellerPrice,
        adults,
        children.length,
        flight.segments,
        formattedPricePerTravellerString,
        formattedTotalPrice,
        i18n,
        isStrikedThroughPricesEnabled,
        priceFrom,
        showPricePerPerson,
        strikedThroughPrice
    ]);
    const priceExplanationAriaLabel = useMemo(() => {
        const travellersCount = adults + children.length;
        let priceExplanation = i18n.trans(t("flights_fd_price_multi"));
        if (localizationUtils.isHungaryDepartingRyanAirFlight(flight.segments)) {
            priceExplanation = i18n.trans(t("flights_ryanair_hungary_bp_price_per", {
                variables: { total_price: formattedTotalPrice, num_travellers: travellersCount }
            }));
        }
        else if (isStrikedThroughPricesEnabled && priceBreakdown.totalWithoutDiscount) {
            return i18n.trans(t("a11y_flights_deal_price_total", {
                variables: {
                    total_price: formattedTotalPrice,
                    old_price: formatPrice(priceBreakdown.totalWithoutDiscount)
                }
            }));
        }
        return `${formattedTotalPrice} ${priceExplanation}`;
    }, [
        adults,
        children.length,
        flight.segments,
        formatPrice,
        formattedTotalPrice,
        i18n,
        isStrikedThroughPricesEnabled,
        priceBreakdown.totalWithoutDiscount
    ]);
    const ariaExplanationAttributes = useMemo(() => {
        const travellersCount = adults + children.length;
        const attributes = {};
        if (travellersCount === 1) {
            attributes["aria-hidden"] = true;
        }
        else {
            attributes["aria-label"] = priceExplanationAriaLabel;
        }
        return attributes;
    }, [adults, children.length, priceExplanationAriaLabel]);
    const shouldHidePriceExplanation = useMemo(() => {
        const isSolo = [...travellerPrices].length === 1;
        return isSolo && ["searchresults", "flightdetails"].includes(routeName);
    }, [travellerPrices, routeName]);
    const travellersCount = adults + children.length;
    const priceTagProps = useMemo(() => {
        const productId = flight.unifiedPriceBreakdown?.id;
        const ticketprice = (isFlexTicket
            ? getFlexibleTicketTotalPriceFromFlight(flight)
            : isCfarTicket
                ? getCfarTicketTotalPriceFromFlight(flight)
                : totalPrice) || totalPrice;
        const priceTranslations = priceFrom
            ? [
                (formattedPrice) => i18n.trans(t("flights_sr_rt_from_price", { variables: { price: formattedPrice } }))
            ]
            : [];
        const strikethroughPrice = priceBreakdown.showPriceStrikethrough
            ? roundPrice && priceBreakdown.totalWithoutDiscountRounded
                ? priceBreakdown.totalWithoutDiscountRounded
                : priceBreakdown.totalWithoutDiscount
            : undefined;
        let strikethroughPricePosition = "end";
        if (!!strikethroughPrice) {
            if (routeName.includes("flightdetails") && (isMobile || isRenderedInModal)) {
                strikethroughPricePosition = "start";
            }
            else if (isMobile || !isRenderedInModal) {
                strikethroughPricePosition = "top";
            }
        }
        return {
            productId,
            price: roundPrice && ticketprice.totalRounded ? ticketprice.totalRounded : ticketprice.total,
            priceTranslations,
            strikethroughPrice,
            strikethroughPricePosition,
            strikethroughPriceProps: { variant: isMobile ? "body_2" : "small_1" },
            shouldRoundPrice: roundPrice
        };
    }, [
        flight,
        i18n,
        isCfarTicket,
        isFlexTicket,
        isMobile,
        isRenderedInModal,
        priceBreakdown,
        priceFrom,
        roundPrice,
        totalPrice,
        routeName
    ]);
    const priceTagPropsWithPricePerAdult = useMemo(() => {
        if (boundsVariant === "inbound") {
            if (!adultTravellerPriceDifference) {
                throw new Error("Split Bounds: Adult perTravellerPriceDifferences was not found");
            }
            const pricePerAdult = roundPrice && adultTravellerPriceDifference.totalRounded
                ? adultTravellerPriceDifference.totalRounded
                : adultTravellerPriceDifference.total;
            if (pricePerAdult.units < 0) {
                splitBoundsTrackingStorage.addValue(flight.token, "price");
            }
            const pricePerAdultTranslations = [
                (formattedPrice) => formattedPrice.replace("-", ""),
                (formattedPrice) => pricePerAdult.units < 0
                    ? i18n.trans(t("flights_sr_split_bounds_us_incremental_minus", { variables: { price: formattedPrice } }))
                    : i18n.trans(t("flights_sr_split_bounds_us_incremental_plus", { variables: { price: formattedPrice } }))
            ];
            if (priceFrom) {
                pricePerAdultTranslations.push((formattedPrice) => i18n.trans(t("flights_sr_rt_from_price", { variables: { price: formattedPrice } })));
            }
            return {
                ...priceTagProps,
                pricePerAdult,
                pricePerAdultTranslations,
                shouldShowPricePerAdult: showPricePerPerson && !!adultTravellerPrice
            };
        }
        if (!adultTravellerPrice)
            return priceTagProps;
        const pricePerAdult = roundPrice && adultTravellerPrice.travellerPriceBreakdown.totalRounded
            ? adultTravellerPrice.travellerPriceBreakdown.totalRounded
            : adultTravellerPrice.travellerPriceBreakdown.total;
        const pricePerAdultTranslations = priceFrom
            ? [
                (formattedPrice) => i18n.trans(t("flights_sr_rt_from_price", { variables: { price: formattedPrice } }))
            ]
            : undefined;
        return {
            ...priceTagProps,
            pricePerAdult,
            pricePerAdultTranslations,
            shouldShowPricePerAdult: showPricePerPerson && !!adultTravellerPrice
        };
    }, [
        adultTravellerPrice,
        adultTravellerPriceDifference,
        boundsVariant,
        flight.token,
        i18n,
        priceFrom,
        priceTagProps,
        roundPrice,
        showPricePerPerson
    ]);
    const unifiedPriceTagPriceProps = useMemo(() => {
        let priceTextVariant;
        if (isSmallerTextVariantForMainPrice) {
            priceTextVariant = "strong_2";
        }
        else if (isMobile && showPricePerPerson && !!adultTravellerPrice && !priceFrom) {
            priceTextVariant = boundsVariant ? "headline_3" : "strong_1";
        }
        else {
            priceTextVariant = "headline_3";
        }
        const pricePropsAttributes = {
            "aria-hidden": true,
            "data-testid": "flight_card_price_main_price"
        };
        return {
            ...priceTagPropsWithPricePerAdult,
            priceAlignment: alignment,
            priceProps: {
                attributes: pricePropsAttributes,
                tagName: totalPriceTagName,
                variant: priceTextVariant
            },
            attachedAncillaries
        };
    }, [
        adultTravellerPrice,
        alignment,
        boundsVariant,
        isMobile,
        priceFrom,
        priceTagPropsWithPricePerAdult,
        isSmallerTextVariantForMainPrice,
        showPricePerPerson,
        totalPriceTagName,
        attachedAncillaries
    ]);
    const unifiedPriceTagPriceBreakdownProps = useMemo(() => {
        if (!shouldShowBreakdown)
            return;
        return {
            priceBreakdownTrigger: React.createElement(Icon, { svg: QuestionMarkCircleIcon, className: styles.uptPriceBreakdownTrigger }),
            priceBreakdownTriggerProps: {
                attributes: {
                    "aria-label": typeof flightIndex === "number"
                        ? i18n.trans(t("a11y_flights_open_price_breakdown_option_index", { variables: { index: flightIndex + 1 } }))
                        : i18n.trans(t("flights_a11y_aria_label_brice_breakdown_question_mark")),
                    "data-testid": "flight_card_price_open_price_popover"
                }
            },
            priceBreakdownContent: React.createElement(UnifiedPriceBreakdownFlightCardPrice, { flight: flight }),
            priceBreakdownCloseAriaLabel: popoverCloseAriaLabel,
            priceBreakdownOnOpen: () => {
                if (routeName.includes("flightdetails")) {
                    trackV2("click_price_breakdown");
                }
                if (isMobile) {
                    resetBodyOverflow();
                }
                onPriceBreakdownOpen?.();
            }
        };
    }, [
        flight,
        flightIndex,
        i18n,
        isMobile,
        onPriceBreakdownOpen,
        popoverCloseAriaLabel,
        routeName,
        shouldShowBreakdown,
        trackV2
    ]);
    const unifiedPriceTagExplanationProps = useMemo(() => {
        if (showPricePerPerson && !!adultTravellerPrice) {
            const pricePerAdultExplanationProps = {
                variant: !!boundsVariant ? "small_1" : "body_2",
                attributes: {
                    "data-testid": "flight_card_price_total_price",
                    ...ariaExplanationAttributes
                }
            };
            const pricePerAdultExplanationTranslations = [];
            if (localizationUtils.isHungaryDepartingRyanAirFlight(flight.segments)) {
                if (variant === "desktop-search-results" ||
                    variant === "mobile-search-results") {
                    pricePerAdultExplanationTranslations.push((formattedPrice) => i18n.trans(t("flights_price_multi_ryanair_hu", { variables: { price: formattedPrice } })));
                }
                else {
                    pricePerAdultExplanationTranslations.push((formattedPrice) => i18n.trans(t("flights_ryanair_hungary_sr_price", { variables: { price: formattedPrice } })));
                }
            }
            else if (!!boundsVariant) {
                pricePerAdultExplanationTranslations.push((formattedPrice) => i18n.trans(t("flights_sr_split_bounds_us_total", { variables: { price: formattedPrice } })));
            }
            else if (variant === "desktop-search-results" ||
                variant === "mobile-search-results") {
                pricePerAdultExplanationTranslations.push((formattedPrice) => i18n.trans(t("flights_sr_price_multi_total", { variables: { total_price: formattedPrice } })));
            }
            else {
                pricePerAdultExplanationTranslations.push((formattedPrice) => i18n.trans(t("flights_sr_price_multi", { variables: { total_price: formattedPrice } })));
            }
            return {
                pricePerAdultExplanationProps,
                pricePerAdultExplanationTranslations
            };
        }
        else if (!shouldHidePriceExplanation) {
            return {
                priceExplanation,
                priceExplanationProps: { attributes: ariaExplanationAttributes }
            };
        }
        return;
    }, [
        showPricePerPerson,
        adultTravellerPrice,
        shouldHidePriceExplanation,
        boundsVariant,
        ariaExplanationAttributes,
        flight.segments,
        variant,
        i18n,
        priceExplanation
    ]);
    const flightCardPrice = (React.createElement(React.Fragment, null,
        React.createElement("div", null,
            showAvailableSeats && React.createElement(FlightAvailableSeats, { flight: flight }),
            !!boundsVariant && (React.createElement(Text, { variant: "small_1", color: "neutral_alt" }, travellersCount > 1
                ? i18n.trans(t("flights_sr_split_bounds_us_round_trip_per"))
                : i18n.trans(t("flights_sr_split_bounds_us_round_trip")))),
            React.createElement(UnifiedPriceTag, { ...unifiedPriceTagPriceProps, ...unifiedPriceTagPriceBreakdownProps, ...unifiedPriceTagExplanationProps, attributes: { role: "region", "aria-label": priceAriaLabel } })),
        isMobile && breakdownOpen && (React.createElement(SheetContainer, { onCloseTrigger: () => setBreakdownOpen(false), title: i18n.trans(t("flights_price_details_title")), closeAriaLabel: i18n.trans(t("close")), active: true, className: styles.drawer },
            React.createElement(UnifiedPriceBreakdownFlightCardPrice, { flight: flight })))));
    return flightCardPrice;
};
export default FlightCardPrice;
